import { useEffect, useState } from "react";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import { Layers, Plus, Pencil, Power, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const emptyForm = { code: "", name: "", description: "", active: true };

export default function CostCenters() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/cost-centers?include_inactive=true").then((r) => setList(r.data));

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setCreating(true); };
  const openEdit = (cc) => { setEditing(cc); setForm({ code: cc.code, name: cc.name, description: cc.description || "", active: cc.active }); };

  const submitCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error(t("costCenters.codeNameRequired")); return; }
    setSaving(true);
    try {
      await api.post("/cost-centers", { code: form.code.trim().toUpperCase(), name: form.name.trim(), description: form.description.trim() });
      toast.success(t("costCenters.created"));
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/cost-centers/${editing.id}`, { name: form.name.trim(), description: form.description.trim(), active: form.active });
      toast.success(t("costCenters.updated"));
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const toggleActive = async (cc) => {
    if (cc.active && !window.confirm(t("costCenters.deactivateConfirm"))) return;
    try {
      await api.put(`/cost-centers/${cc.id}`, { active: !cc.active });
      toast.success(cc.active ? t("costCenters.deactivated") : t("costCenters.reactivated"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <>
      <Topbar title={t("costCenters.title")} subtitle={t("costCenters.subtitle", { count: list.length })} />
      <div className="p-6 pb-28 space-y-5">
        <div className="flex justify-end">
          <button data-testid="cc-new-btn" onClick={openCreate} className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> {t("costCenters.newCC")}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("costCenters.code")}</th>
                <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("costCenters.name")}</th>
                <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("costCenters.description")}</th>
                <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("costCenters.status")}</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((cc) => (
                <tr key={cc.id} data-testid={`cc-row-${cc.code}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${cc.active ? "" : "opacity-50"}`}>
                  <td className="px-6 py-3.5">
                    <span className="inline-flex items-center gap-1.5 font-mono text-sm font-bold text-indigo-700">
                      <Layers className="w-3.5 h-3.5" />{cc.code}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-sm font-semibold">{cc.name}</td>
                  <td className="px-6 py-3.5 text-sm text-slate-500 max-w-md">{cc.description}</td>
                  <td className="px-6 py-3.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cc.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                      {cc.active ? t("costCenters.active") : t("costCenters.inactive")}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-right whitespace-nowrap">
                    <button data-testid={`cc-edit-${cc.code}`} onClick={() => openEdit(cc)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 border border-slate-200 mr-2">
                      <Pencil className="w-3.5 h-3.5" /> {t("common.edit")}
                    </button>
                    <button data-testid={`cc-toggle-${cc.code}`} onClick={() => toggleActive(cc)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${cc.active ? "text-red-600 border-red-200 hover:bg-red-50" : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"}`}>
                      <Power className="w-3.5 h-3.5" /> {cc.active ? t("costCenters.deactivate") : t("costCenters.reactivate")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="cc-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-display font-bold text-lg">{creating ? t("costCenters.newCC") : editing.name}</h3>
              <button onClick={() => { setCreating(false); setEditing(null); }} data-testid="cc-modal-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("costCenters.code")}</label>
                <input
                  data-testid="cc-modal-code"
                  type="text"
                  value={form.code}
                  disabled={!creating}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#FFDF00] disabled:bg-slate-100 disabled:text-slate-500"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("costCenters.name")}</label>
                <input data-testid="cc-modal-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("costCenters.description")}</label>
                <textarea data-testid="cc-modal-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input data-testid="cc-modal-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#FFDF00]" />
                  {t("costCenters.active")}
                </label>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => { setCreating(false); setEditing(null); }} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
              <button data-testid="cc-modal-save" onClick={creating ? submitCreate : submitEdit} disabled={saving} className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                {saving ? t("common.saving") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
